import { addDays, daysBetween } from './dates.mjs';

// One level of grouping: children are ordinary items in their group's lane.
export const syncGroups = (items) => {
  const groups = new Map(items.filter((item) => item.isGroup).map((item) => [item.id, item]));
  for (const item of items) {
    const parent = groups.get(item.parentId);
    if (item.parentId && (item.isGroup || !parent || parent.id === item.id || parent.laneId !== item.laneId)) item.parentId = '';
  }
  for (const group of groups.values()) {
    // A linked group's own dates and its visual envelope are DIFFERENT things.
    // In particular, taking a task's due date cannot visually shorten a group
    // beyond its child items. Preserve the explicitly chosen date separately.
    if (!group.ownStartDate) group.ownStartDate = group.startDate;
    if (!group.ownEndDate) group.ownEndDate = group.endDate;
    const children = items.filter((item) => item.parentId === group.id);
    group.startDate = children.reduce((start, item) => item.startDate < start ? item.startDate : start, group.ownStartDate);
    group.endDate = children.reduce((end, item) => item.endDate > end ? item.endDate : end, group.ownEndDate);
    group.noStart = false;
  }
  return items;
};

// Group moves translate their contents. Child edits only affect that child.
export const applyItemChange = (items, value, { moveChildren = false } = {}) => {
  const previous = items.find((item) => item.id === value.id);
  if (previous?.isGroup) {
    const delta = daysBetween(value.startDate, previous.startDate);
    if (moveChildren) {
      // Dragging the whole group shifts both its own plan and its children.
      value.ownStartDate = addDays(previous.ownStartDate || previous.startDate, delta);
      value.ownEndDate = addDays(previous.ownEndDate || previous.endDate, delta);
    } else {
      // Modal edits carry explicit own dates. Candidate normalization may have
      // expanded their VISIBLE values; never copy those expanded values back
      // into ownStartDate/ownEndDate on the second applyItemChange call.
      // A direct SVG resize without explicit own dates still edits the plan.
      const startWasResized = value.startDate !== previous.startDate
        && value.ownStartDate === previous.ownStartDate;
      const endWasResized = value.endDate !== previous.endDate
        && value.ownEndDate === previous.ownEndDate;
      value.ownStartDate = startWasResized ? value.startDate : (value.ownStartDate || value.startDate);
      value.ownEndDate = endWasResized ? value.endDate : (value.ownEndDate || value.endDate);
    }
    for (const child of items.filter((item) => item.parentId === previous.id)) {
      if (!value.isGroup) { child.parentId = ''; child.row = null; }
      else {
        child.laneId = value.laneId;
        if (moveChildren) {
          child.startDate = addDays(child.startDate, delta);
          child.endDate = addDays(child.endDate, delta);
        }
      }
    }
  }
  if (previous && ((previous.parentId || '') !== (value.parentId || '') || previous.laneId !== value.laneId)) value.row = null;
  if (!previous && value.isGroup) {
    value.ownStartDate = value.startDate;
    value.ownEndDate = value.endDate;
  }
  if (previous) Object.assign(previous, value); else items.push(value);
  syncGroups(items);
};
